import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { ChevronLeft, ChevronRight, FastForward, Pause, Play, SkipBack, SkipForward } from "lucide-react";
import { useAudio } from "../../../context/AudioContext";
import { useLocation, useNavigate } from "react-router";
import AudioProgressBar from "./AudioProgressBar";
import Loading from "../Loading/Loading";
import { FastForwardSharp, FastRewind, FastRewindSharp } from "@mui/icons-material";

const AudioPlayer = ({ 
  streamingData, 
  data, 
  onFetchStreamingData, 
  streamingLoading, 
  userRequestedStreaming 
}) => {
  
  const [isBuffering, setIsBuffering] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [playRequestInProgress, setPlayRequestInProgress] = useState(false);
  
  const location = useLocation();
  const audioContext = useAudio();
  const navigate = useNavigate();
  
  // Refs for preventing multiple simultaneous operations
  const playRequestRef = useRef(false);
  const lastClickTimeRef = useRef(0);
  const eventListenersAddedRef = useRef(false);

  const isCurrentAudio = audioContext.audioData.audio_id === data.audio_id;

  // Memoize streaming data checks to prevent unnecessary recalculations
  const isStreamingDataStale = useMemo(() => {
    return streamingData && streamingData.audio_id !== data?.audio_id;
  }, [streamingData?.audio_id, data?.audio_id]);

  // Debounced click handler to prevent rapid clicks
  const debounce = useCallback((func, delay) => {
    return (...args) => {
      const now = Date.now();
      if (now - lastClickTimeRef.current > delay) {
        lastClickTimeRef.current = now;
        func(...args);
      }
    };
  }, []);

  // FIXED: Reset function to clear stuck states
  const resetPlayerState = useCallback(() => {
    console.log("Resetting player state");
    setPlayRequestInProgress(false);
    setIsBuffering(false);
    playRequestRef.current = false;
  }, []);

  // Reset state when audio_id changes
  useEffect(() => {
    resetPlayerState();
  }, [data?.audio_id, resetPlayerState]);

  // FIXED: Enhanced event handlers with better state management
  const eventHandlers = useMemo(() => ({
    handleWaiting: () => {
      console.log("Audio waiting for data - buffering...");
      setIsBuffering(true);
    },
    
    handleCanPlay: () => {
      console.log("Audio can play - enough data buffered");
      setIsBuffering(false);
    },
    
    handlePlaying: () => {
      console.log("Audio started playing successfully");
      setIsBuffering(false);
      setPlayRequestInProgress(false); // Ensure this always resets
      // Sync audio context state with actual audio element
      if (audioContext.audioRef?.current && !audioContext.audioRef.current.paused) {
        audioContext.setAudioPlay(true);
      }
    },
    
    handlePause: () => {
      console.log("Audio paused");
      audioContext.setAudioPlay(false);
      setPlayRequestInProgress(false);
    },
    
    handleError: (e) => {
      console.error("Audio error:", e);
      setIsBuffering(false);
      setPlayRequestInProgress(false);
      audioContext.setAudioPlay(false);
    }
  }), [audioContext]);

  // FIXED: Enhanced audio event handling with pause handler
  useEffect(() => {
    const audio = audioContext.audioRef?.current;
    
    // Only add event listeners if we have audio element and haven't added them yet
    if (audio && !eventListenersAddedRef.current && !isNavigating) {
      console.log("Adding audio event listeners");
      
      // Add event listeners including pause handler
      audio.addEventListener('waiting', eventHandlers.handleWaiting);
      audio.addEventListener('canplay', eventHandlers.handleCanPlay);
      audio.addEventListener('canplaythrough', eventHandlers.handleCanPlay);
      audio.addEventListener('playing', eventHandlers.handlePlaying);
      audio.addEventListener('pause', eventHandlers.handlePause); // ADDED
      audio.addEventListener('error', eventHandlers.handleError);
      audio.addEventListener('stalled', eventHandlers.handleWaiting);
      
      eventListenersAddedRef.current = true;
    }

    // Cleanup function
    return () => {
      if (audio && eventListenersAddedRef.current) {
        console.log("Removing audio event listeners");
        audio.removeEventListener('waiting', eventHandlers.handleWaiting);
        audio.removeEventListener('canplay', eventHandlers.handleCanPlay);
        audio.removeEventListener('canplaythrough', eventHandlers.handleCanPlay);
        audio.removeEventListener('playing', eventHandlers.handlePlaying);
        audio.removeEventListener('pause', eventHandlers.handlePause); // ADDED
        audio.removeEventListener('error', eventHandlers.handleError);
        audio.removeEventListener('stalled', eventHandlers.handleWaiting);
        eventListenersAddedRef.current = false;
      }
    };
  }, [audioContext.audioRef?.current, eventHandlers, isNavigating]);

  // FIXED: Auto-play when streaming data arrives - with better dependency management
  useEffect(() => {
    // Only run if conditions are met and avoid unnecessary calls
    if (
      streamingData &&
      userRequestedStreaming &&
      !isNavigating &&
      !isStreamingDataStale &&
      !playRequestRef.current &&
      streamingData.audio_id === data?.audio_id // Extra safety check
    ) {
      console.log("Auto-playing with new streaming data");
      
      const audioUrl = streamingData?.streaming_link || streamingData?.download_link;
      if (audioUrl) {
        playAudioWithUrl(audioUrl);
      }
    }
  }, [
    streamingData?.audio_id, // Only track audio_id changes, not the entire object
    userRequestedStreaming, 
    isNavigating, 
    isStreamingDataStale,
    data?.audio_id
  ]);

  // FIXED: Optimized audio playing function with stable dependencies
  const playAudioWithUrl = useCallback(async (audioUrl) => {
    if (!audioContext.audioRef?.current || isNavigating || playRequestRef.current) {
      return false;
    }

    const audio = audioContext.audioRef.current;
    playRequestRef.current = true;
    
    try {
      setPlayRequestInProgress(true);
      setIsBuffering(true);
      
      // Set audio data in context
      audioContext.setAudioData({
        audio_id: data?.audio_id,
        title: data?.title,
        author: data?.author,
        reader: data?.reader,
        image: data?.image,
        url: audioUrl,
        series: data?.series,
        route: location.pathname,
      });
      
      // Set audio properties
      audio.volume = 1.0;
      audio.muted = false;
      audio.preload = 'auto';
      
      // Set the source if it's different
      if (audio.src !== audioUrl) {
        console.log("Setting new audio source:", audioUrl);
        audio.src = audioUrl;
        audio.load();
      }
      
      // Try to play
      await audio.play();
      audioContext.setAudioPlay(true);
      console.log("Audio play successful!");
      return true;
      
    } catch (error) {
      console.error("Audio play failed:", error);
      setPlayRequestInProgress(false);
      setIsBuffering(false);
      return false;
    } finally {
      playRequestRef.current = false;
    }
  }, [audioContext, data?.audio_id, data?.title, data?.author, data?.reader, data?.image, data?.series, location.pathname, isNavigating]);

  // **FIXED: OPTIMIZED HANDLE PLAY FUNCTION with proper state management**
  const handlePlay = useMemo(() => debounce(async () => {
    console.log("=== Play button clicked ===");
    
    // Prevent multiple simultaneous requests
    if (playRequestInProgress || playRequestRef.current || isNavigating) {
      console.log("Play request already in progress or navigating, ignoring...");
      return;
    }
    
    console.log("isCurrentAudio:", isCurrentAudio);
    console.log("streamingData:", streamingData ? "exists" : "null");
    console.log("isStreamingDataStale:", isStreamingDataStale);
    
    // Case 1: Current audio - toggle play/pause
    if (isCurrentAudio) {
      console.log("Toggling current audio play/pause");
      if (audioContext.audioRef?.current) {
        const audio = audioContext.audioRef.current;
        
        if (audio.paused) {
          try {
            setPlayRequestInProgress(true);
            await audio.play();
            audioContext.setAudioPlay(true);
            console.log("Resumed current audio");
            // FIX: Always reset playRequestInProgress on success
            setPlayRequestInProgress(false);
          } catch (error) {
            console.error("Resume play failed:", error);
            setPlayRequestInProgress(false);
          }
        } else {
          audio.pause();
          audioContext.setAudioPlay(false);
          console.log("Paused current audio");
        }
      }
      return;
    }
    
    // Case 2: Have valid streaming data - play immediately
    if (streamingData && !isStreamingDataStale) {
      console.log("Playing with existing valid streaming data");
      
      const audioUrl = streamingData?.streaming_link || streamingData?.download_link;
      if (audioUrl) {
        await playAudioWithUrl(audioUrl);
      } else {
        console.error("No valid audio URL in streaming data");
      }
      return;
    }
    
    // Case 3: Need to fetch streaming data
    if (onFetchStreamingData) {
      console.log("Fetching streaming data...");
      setPlayRequestInProgress(true);

      try {
        const audioUrl = await onFetchStreamingData();
        if (audioUrl && !isNavigating) {
          await playAudioWithUrl(audioUrl);
        } else {
          setPlayRequestInProgress(false);
        }
      } catch (error) {
        console.error("Failed to fetch streaming data:", error);
        setPlayRequestInProgress(false);
      }
      return;
    }

    // Case 4: No way to play
    console.warn("No valid way to play audio");
  }, 300), [isCurrentAudio, streamingData, isStreamingDataStale, onFetchStreamingData, playAudioWithUrl, audioContext, isNavigating, playRequestInProgress, debounce]);

  // FIXED: Memoize skip functions to prevent recreation
  const skipForward = useCallback(() => {
    if (audioContext.audioRef.current && !isNavigating) {
      audioContext.audioRef.current.currentTime = Math.min(
        audioContext.audioRef.current.currentTime + 15,
        audioContext.audioRef.current.duration || 0
      );
    }
  }, [audioContext.audioRef, isNavigating]);

  const skipBackward = useCallback(() => {
    if (audioContext.audioRef.current && !isNavigating) {
      audioContext.audioRef.current.currentTime = Math.max(
        audioContext.audioRef.current.currentTime - 15,
        0
      );
    }
  }, [audioContext.audioRef, isNavigating]);
  
  // FIXED: Navigation function with proper cleanup and better state management
  const handleClickRelatedItem = useCallback(async (audio_uniq_idx) => {
    console.log("Navigating to next episode:", audio_uniq_idx);
    
    // Set navigation flag
    setIsNavigating(true);
    setPlayRequestInProgress(false);
    playRequestRef.current = false;
    
    // Stop and reset current audio
    if (audioContext.audioRef.current) {
      const audio = audioContext.audioRef.current;
      audio.pause();
      audio.currentTime = 0;
      audio.src = '';
      audio.load();
    }
  
    // Reset audio context
    audioContext.setAudioPlay(false);
    audioContext.setAudioData({
      audio_id: null,
      title: '',
      author: '',
      reader: '',
      image: '',
      url: '',
      series: '',
      route: '',
    });
  
    // Reset local states
    setIsBuffering(false);
    
    // Navigate
    await new Promise(resolve => setTimeout(resolve, 100));
    navigate(`/audio/book-detail/${audio_uniq_idx}`, { replace: false });
    
    // Reset navigation flag
    setTimeout(() => {
      setIsNavigating(false);
    }, 500);
  }, [audioContext, navigate]);

  // Memoize computed values to prevent unnecessary recalculations
  const showLoading = useMemo(() => 
    (playRequestInProgress || streamingLoading || isBuffering) && !isNavigating,
    [playRequestInProgress, streamingLoading, isBuffering, isNavigating]
  );

  const isDisabled = useMemo(() => 
    (!isCurrentAudio && (!streamingData || isStreamingDataStale) && !onFetchStreamingData) || isNavigating,
    [isCurrentAudio, streamingData, isStreamingDataStale, onFetchStreamingData, isNavigating]
  );

  return (
    <div className="space-y-2 my-3">
      {/* Status indicators */}
      {isBuffering && !isNavigating && (
        <div className="px-4 mb-2">
          <div className="text-center text-yellow-400 text-sm">
            Buffering audio...
          </div>
        </div>
      )}

      {isNavigating && (
        <div className="px-4 mb-2">
          <div className="text-center text-blue-400 text-sm">
            Loading next episode...
          </div>
        </div>
      )}

      {audioContext.audioRef && !isNavigating && (
        <AudioProgressBar
          musicData={data}
          musicRef={audioContext.audioRef}
          setIsLoading={setPlayRequestInProgress}
        />
      )}

      <div className="flex justify-center py-3">
        <div className="flex items-center space-x-3">
          
          {/* Previous Episode */}
          {data.first_episode ? (
              // Render placeholder space
              <div className="p-2 w-[40px] h-[40px]" />
            ) : (
              <button
                onClick={() => handleClickRelatedItem(data.prev_audio_id)}
                disabled={isNavigating}
                className={`text-yellow-400 p-2 rounded-full  transition ${isNavigating ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <SkipBack size={24} />
              </button>
            )}

          {/* Skip Back */}
          <button
            onClick={skipBackward}
            disabled={isNavigating}
            className={`text-yellow-400 p-2 rounded-full  transition ${isNavigating ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <FastRewindSharp size={24} />
          </button>

          {/* Play / Pause Button */}
          <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center shadow">
            {isDisabled ? (
              <Play className="h-6 w-6 text-gray-500 opacity-50" />
            ) : isCurrentAudio && !isNavigating ? (
              audioContext.audioPlay ? (
                <Pause
                  className="h-6 w-6 text-gray-900 cursor-pointer"
                  onClick={audioContext.pauseAudio}
                />
              ) : (
                <Play
                  className="h-6 w-6 text-gray-900 cursor-pointer"
                  onClick={handlePlay}
                />
              )
            ) : showLoading || isNavigating ? (
              <div className="w-6 h-6 border-2 border-gray-900 border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Play
                className="h-6 w-6 text-gray-900 cursor-pointer"
                onClick={handlePlay}
              />
            )}
          </div>

          {/* Skip Forward */}
          <button
            onClick={skipForward}
            disabled={isNavigating}
            className={`text-yellow-400 p-2 rounded-full  transition ${isNavigating ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <FastForwardSharp size={24} />
          </button>

          {/* Next Episode */}
          {data.last_episode ? (
              <div className="p-2 w-[40px] h-[40px]" />
            ) : (
              <button
                onClick={() => handleClickRelatedItem(data.next_audio_id)}
                disabled={isNavigating}
                className={`text-yellow-400 p-2 rounded-full  transition ${isNavigating ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <SkipForward size={24} />
              </button>
            )}

        </div>
      </div>

      {/* Loading Status */}
      {(showLoading || isNavigating) && (
        <div className="text-center text-xs text-gray-400">
          {isNavigating ? 'Loading next episode...' :
           isBuffering ? 'Buffering audio...' : 
           streamingLoading ? 'Preparing audio...' : 
           playRequestInProgress ? 'Loading audio...' : 'Loading...'
           }
        </div>
      )}
    </div>
  );
};

export default AudioPlayer;